import {
    Defs,
    Ellipse,
    Image,
    RadialGradient,
    Rect,
    Stop,
    Svg,
    Text,
    View,
} from "@react-pdf/renderer";
import { A4, ACCENT, asset } from "@/components/pdf/PdfBrand";
import { ROCKET_PROGRAMME } from "@/lib/programmes/rocket";

// La charte pastel des pages accélérateurs, pour les documents PDF :
// programme de formation, livret d'accueil, justificatif d'entrée et
// attestation de fin de formation. Fonds lavande, taches de couleur en
// dégradé radial (react-pdf ne sait pas flouter), BeBot et les objets du
// hero de chaque formation, étiquettes violettes, un seul accent orange.
//
// Les images viennent de public/images/programmes/<slug>/ : l'archivage
// serveur les embarque grâce à outputFileTracingIncludes (next.config.js).

export const VIOLET = "#613BDB";
export const LAVANDE = "#F8F6FF";
export const LAVANDE_CARTE = "#F4F0FF";
export const LAVANDE_FILET = "#E4DCFF";

export const imgProgramme = (slug, fichier) =>
    asset(`/images/programmes/${slug}/${fichier}`);

// Le slug de la formation : celui du programme quand il est connu, sinon
// déduit de son intitulé (les anciennes réponses de l'API n'ont pas de slug).
export const slugFormation = (program) => {
    const slug = program?.slug;
    if (slug === "rocket" || slug === "python") return slug;
    return /python/i.test(program?.name || "") ? "python" : "rocket";
};

// Les taches de couleur des pages pleines : [couleur, x, y, rayon].
export const TACHES = {
    couverture: [
        ["#E4DCFF", 70, 130, 300],
        ["#D8ECFF", 560, 250, 300],
        ["#DDF6EA", 170, 640, 280],
        ["#FFF1C9", 540, 760, 220],
    ],
    1: [
        ["#E4DCFF", 520, 90, 300],
        ["#DDF6EA", 40, 420, 260],
        ["#FFF1C9", 480, 760, 240],
    ],
    2: [
        ["#D8ECFF", 60, 110, 300],
        ["#E4DCFF", 560, 520, 280],
        ["#DDF6EA", 150, 790, 240],
    ],
    3: [
        ["#DDF6EA", 540, 120, 300],
        ["#FFF1C9", 40, 480, 250],
        ["#E4DCFF", 420, 800, 260],
    ],
    fin: [
        ["#E4DCFF", 90, 160, 300],
        ["#D8ECFF", 540, 420, 300],
        ["#FFF1C9", 160, 740, 260],
    ],
    // Le bandeau d'en-tête des documents d'une page.
    bandeau: [
        ["#E4DCFF", 60, 30, 190],
        ["#D8ECFF", 330, 150, 170],
        ["#FFF1C9", 560, 20, 150],
        ["#DDF6EA", 520, 190, 130],
    ],
};

// Un fond lavande et ses taches, sur toute la page ou sur un bandeau.
export function FondPastel({
    taches,
    largeur = A4.largeur + 1,
    hauteur = A4.hauteur + 1,
    id = "tache",
}) {
    return (
        <Svg
            width={largeur}
            height={hauteur}
            style={{ position: "absolute", top: 0, left: 0 }}
        >
            <Defs>
                {taches.map(([couleur], i) => (
                    <RadialGradient key={i} id={`${id}${i}`} cx="0.5" cy="0.5" r="0.5">
                        <Stop offset="0" stopColor={couleur} stopOpacity={1} />
                        <Stop offset="0.55" stopColor={couleur} stopOpacity={0.75} />
                        <Stop offset="1" stopColor={couleur} stopOpacity={0} />
                    </RadialGradient>
                ))}
            </Defs>
            <Rect x={0} y={0} width={largeur} height={hauteur} fill={LAVANDE} />
            {taches.map(([, x, y, r], i) => (
                <Ellipse key={i} cx={x} cy={y} rx={r} ry={r} fill={`url(#${id}${i})`} />
            ))}
        </Svg>
    );
}

// Les objets qui entourent BeBot, repris du hero de chaque page :
// [fichier, x, y, largeur, angle], pour une scène de 474 × 420.
const OBJETS_PYTHON = [
    ["enveloppe", 18, 70, 86, -10],
    ["graphique", 380, 40, 92, 8],
    ["tableur", 0, 250, 84, -6],
    ["dossier", 392, 210, 78, 6],
    ["calendrier", 70, 330, 62, -8],
    ["avion", 110, 0, 70, -4],
    ["code", 360, 330, 82, 5],
];
export const OBJETS_COUVERTURE = {
    python: OBJETS_PYTHON,
    rocket: ROCKET_PROGRAMME.coverObjects,
};

// BeBot au centre d'une forme pastel, entouré des objets de sa formation.
// Les positions des objets sont données pour 474 × 420 et suivent l'échelle.
export function ScenePastel({
    slug,
    largeur = 474,
    hauteur = 420,
    bebot = "bebot.png",
    objets,
    id = "forme",
    // Par défaut, les objets suivent la largeur de la scène ; un appelant
    // qui donne ses propres positions peut les garder telles quelles (1).
    echelle = largeur / 474,
}) {
    objets = objets || OBJETS_COUVERTURE[slug] || OBJETS_PYTHON;
    const hero = (fichier) => imgProgramme(slug, `hero/${fichier}`);
    const largeurBebot = largeur * 0.46;
    return (
        <View style={{ width: largeur, height: hauteur, position: "relative" }}>
            <Svg
                width={largeur}
                height={hauteur}
                style={{ position: "absolute", top: 0, left: 0 }}
            >
                <Defs>
                    <RadialGradient id={id} cx="0.35" cy="0.3" r="0.8">
                        <Stop offset="0" stopColor="#E6DEFF" />
                        <Stop offset="0.6" stopColor="#DCE9FF" />
                        <Stop offset="1" stopColor="#DDF6EA" />
                    </RadialGradient>
                </Defs>
                <Ellipse
                    cx={largeur / 2}
                    cy={hauteur * 0.5}
                    rx={largeur * 0.36}
                    ry={hauteur * 0.42}
                    fill={`url(#${id})`}
                />
                <Ellipse cx={largeur * 0.2} cy={hauteur * 0.78} rx={28 * Math.min(1, largeur / 474)} ry={28 * Math.min(1, largeur / 474)} fill="#FFE9B8" />
                <Ellipse cx={largeur * 0.8} cy={hauteur * 0.2} rx={18 * Math.min(1, largeur / 474)} ry={18 * Math.min(1, largeur / 474)} fill="#FFD9CC" />
            </Svg>
            <Image
                src={hero(bebot)}
                style={{
                    position: "absolute",
                    width: largeurBebot,
                    left: (largeur - largeurBebot) / 2,
                    top: hauteur * 0.1,
                }}
            />
            {objets.map(([fichier, x, y, w, angle]) => (
                <Image
                    key={fichier}
                    src={hero(`${fichier}.png`)}
                    style={{
                        position: "absolute",
                        left: x * echelle,
                        top: y * echelle,
                        width: w * echelle,
                        transform: `rotate(${angle}deg)`,
                    }}
                />
            ))}
        </View>
    );
}

// L'étiquette des pages : un petit carré orange puis un libellé en chasse
// fixe, violet par défaut.
export function EtiquettePastel({ children, couleur = VIOLET, style }) {
    return (
        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8, ...style }}>
            <View style={{ width: 6, height: 6, backgroundColor: ACCENT, marginRight: 8 }} />
            <Text
                style={{
                    fontFamily: "SourceCode",
                    fontSize: 7.5,
                    lineHeight: 1,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: couleur,
                }}
            >
                {children}
            </Text>
        </View>
    );
}
